import {
  Avatar,
  Autocomplete,
  Box,
  FormControl,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { useState, useMemo } from 'react';
import { LineData } from '../../resources/ScenarioResource';
import ParticipantResource from '../../resources/ParticipantResource';
import ParticipantFormModal from './ParticipantFormModal';
import useConfirmation from '../../hooks/useConfirmation';
import { useRequester } from '../../hooks';

type Props = {
  line: LineData;
  index: number;
  onUpdate: (index: number, updatedLine: LineData) => void;
  onDelete: (index: number) => void;
  canDelete: boolean;
};

const CREATE_NEW_OPTION = '__CREATE_NEW__';

export default function LineInputForm({
  line,
  index,
  onUpdate,
  onDelete,
  canDelete,
}: Props) {
  const [searchText, setSearchText] = useState('');
  const [participantModalOpen, setParticipantModalOpen] = useState(false);
  const [editingParticipant, setEditingParticipant] = useState<ParticipantResource | null>(null);
  const participants = ParticipantResource.list();
  const fetchParticipants = useRequester<ParticipantResource[]>({
    onSubmit: () => ParticipantResource.fetchMany(),
    onError: console.log,
  });

  const deleteParticipantConfirmation = useConfirmation<ParticipantResource>({
    getTitle: (participant) => `Delete Participant "${participant.data.name}"?`,
    getDescription: (participant) =>
      `Are you sure you want to delete "${participant.data.name}"? This action cannot be undone.`,
    onConfirm: async (participant) => {
      try {
        await ParticipantResource.deleteOne(participant.data.id);
        ParticipantResource.showAlert('Participant deleted successfully', 'success');
        // If the deleted participant was selected for this line, clear it
        if (line.participantId === participant.data.id) {
          onUpdate(index, { ...line, participantId: '' });
        }
      } catch (error) {
        ParticipantResource.showAlert('Failed to delete participant', 'error');
      }
    },
  });

  const handleParticipantChange = (participantId: string) => {
    if (participantId === CREATE_NEW_OPTION) {
      setEditingParticipant(null);
      setParticipantModalOpen(true);
      return;
    }
    onUpdate(index, { ...line, participantId });
  };

  const handleTextChange = (text: string) => {
    onUpdate(index, { ...line, text });
  };

  const handleTranslationChange = (translation: string) => {
    onUpdate(index, { ...line, translation });
  };

  const handleParticipantSaved = (participant: ParticipantResource) => {
    // If we're creating a new participant, update the line to point to it.
    // For edits, we keep the existing participant linkage for this line.
    if (!editingParticipant) {
      onUpdate(index, { ...line, participantId: participant.data.id });
    }
    setParticipantModalOpen(false);
    setEditingParticipant(null);
    fetchParticipants.submit(); // Refresh participants list
  };

  // Filter participants based on search text
  const filteredParticipants = !searchText.trim()
    ? participants
    : participants.filter(
      (p) =>
        p.data.name.toLowerCase().includes(searchText.toLowerCase()) ||
        p.data.role?.toLowerCase().includes(searchText.toLowerCase())
    );

  // Create options for Autocomplete
  const options = useMemo(() => {
    const participantOptions = filteredParticipants.map((p) => ({
      id: p.data.id,
      label: p.data.name,
      role: p.data.role,
      avatar: p.data.avatar,
    }));
    // Add "Create New" option at the end
    return [
      ...participantOptions,
      {
        id: CREATE_NEW_OPTION,
        label: 'Create New',
        role: '',
        avatar: '',
      },
    ];
  }, [filteredParticipants]);

  const participant = participants.find((p) => p.data.id === line.participantId);
  const selectedParticipant = participant ? {
    id: participant.data.id,
    label: participant.data.name,
    role: participant.data.role,
    avatar: participant.data.avatar,
  } : null;

  return (
    <Box className="flex flex-col gap-4 p-4 border rounded">
      <Box display="flex" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">Line {index + 1}</Typography>
        <IconButton
          size="small"
          color="error"
          onClick={() => onDelete(index)}
          disabled={!canDelete}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>

      <FormControl fullWidth required>
        <Autocomplete
          value={selectedParticipant}
          onChange={(_, newValue) => {
            handleParticipantChange(newValue?.id || null);
          }}
          onInputChange={(_, newInputValue) => {
            setSearchText(newInputValue);
          }}
          options={options}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Participant"
              value={selectedParticipant?.label || ''}
              required
              InputProps={{
                ...params.InputProps,
                startAdornment: selectedParticipant ? (
                  <InputAdornment position="start">
                    <Avatar
                      src={selectedParticipant.avatar}
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: 'primary.main',
                        fontSize: '0.875rem',
                      }}
                    >
                      {selectedParticipant.label.charAt(0).toUpperCase()}
                    </Avatar>
                  </InputAdornment>
                ) : null,
              }}
            />
          )}
          renderOption={(props, option) => {
            if (option.id === CREATE_NEW_OPTION) {
              return (
                <Box component="li" {...props} key={option.id}>
                  <Typography
                    variant="body2"
                    sx={{ fontStyle: 'italic', color: 'primary.main' }}
                  >
                    + {option.label}
                  </Typography>
                </Box>
              );
            }
            return (
              <Box component="li" {...props} key={option.id}>
                <Box className="flex flex-row items-center gap-2 w-full">
                  <Avatar
                    src={option.avatar}
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: 'primary.main',
                      fontSize: '0.75rem',
                    }}
                  >
                    {option.label.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box className="flex flex-col flex-grow">
                    <Typography variant="body2" fontWeight="500">
                      {option.label}
                    </Typography>
                    {option.role && (
                      <Typography variant="caption" color="text.secondary">
                        {option.role}
                      </Typography>
                    )}
                  </Box>
                  <IconButton
                    size="small"
                    edge="end"
                    onClick={(event) => {
                      event.stopPropagation();
                      event.preventDefault();
                      const participantToEdit = participants.find(
                        (p) => p.data.id === option.id
                      );
                      if (participantToEdit) {
                        setEditingParticipant(participantToEdit);
                        setParticipantModalOpen(true);
                      }
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    edge="end"
                    color="error"
                    onClick={(event) => {
                      event.stopPropagation();
                      event.preventDefault();
                      const participantToDelete = participants.find(
                        (p) => p.data.id === option.id
                      );
                      if (participantToDelete) {
                        deleteParticipantConfirmation.triggerFor(participantToDelete);
                      }
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            );
          }}
        />
      </FormControl>

      <ParticipantFormModal
        open={participantModalOpen}
        onClose={() => {
          setParticipantModalOpen(false);
          setEditingParticipant(null);
        }}
        onSuccess={handleParticipantSaved}
        participant={editingParticipant}
      />

      {/* Text and Translation below */}
      <Box className="flex flex-col gap-4">
        <TextField
          label="Text"
          value={line.text}
          onChange={(e) => handleTextChange(e.target.value)}
          required
          multiline
          rows={2}
          fullWidth
        />
        <TextField
          label="Translation (Optional)"
          value={line.translation || ''}
          onChange={(e) => handleTranslationChange(e.target.value)}
          multiline
          rows={2}
          fullWidth
        />
      </Box>
    </Box>
  );
}
